import type { TokenRegistryEntry } from './types';

export function normalizeContractAddress(addr: string): string {
  return String(addr || '')
    .trim()
    .toLowerCase();
}

/**
 * Explorer CSV/API often omits token metadata: empty symbol and tokenDecimal 0 or blank.
 * Using 0 decimals then shows raw wei as the "amount". Default to 18 when decimals are
 * 0 and the symbol is missing; keep 0 when a symbol is present (true 0-decimal tokens).
 */
export function normalizeTokenDecimalsFromExplorerRow(
  row: Record<string, unknown>
): number {
  const raw = row.tokenDecimal ?? row.token_decimals;
  if (raw === '' || raw === null || raw === undefined) return 18;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return 18;
  const rawSym = String(row.tokenSymbol ?? row.token_symbol ?? '').trim();
  if (n === 0 && !rawSym) return 18;
  return n;
}

export function displayTokenSymbolFromExplorerRow(
  row: Record<string, unknown>,
  contractAddr: string
): string {
  const rawSym = String(row.tokenSymbol ?? row.token_symbol ?? '').trim();
  if (rawSym) return rawSym.toUpperCase();
  if (contractAddr) {
    return `ERC20(${contractAddr.slice(0, 6)}…${contractAddr.slice(-4)})`.toUpperCase();
  }
  return '?';
}

/**
 * Build a deduplicated token registry from wallet-fetched tokentx rows (no preloaded list).
 */
export function buildTokenRegistryFromTokentxRows(
  rows: Record<string, unknown>[],
  ownedSet: Set<string>
): Map<string, TokenRegistryEntry> {
  const map = new Map<string, TokenRegistryEntry>();

  for (const row of rows) {
    const c = normalizeContractAddress(
      String(row.contractAddress ?? row.contract_address ?? '')
    );
    if (!c || !c.startsWith('0x')) continue;

    const sym =
      String(row.tokenSymbol ?? row.token_symbol ?? '').trim() ||
      (c ? `ERC20(${c.slice(0, 6)}…${c.slice(-4)})` : '?');
    const name = String(row.tokenName ?? row.token_name ?? '').trim() || sym;
    const decimals = normalizeTokenDecimalsFromExplorerRow(row);
    const tsSec = Number(row.timeStamp ?? row.timestamp ?? 0);
    const ts = tsSec * 1000;
    const from = String(row.from ?? '').toLowerCase();
    const to = String(row.to ?? '').toLowerCase();

    const prev = map.get(c);
    const ownedTouch: string[] = [];
    if (ownedSet.has(from)) ownedTouch.push(from);
    if (ownedSet.has(to)) ownedTouch.push(to);

    if (!prev) {
      map.set(c, {
        contractAddress: c,
        tokenSymbol: sym,
        tokenName: name,
        decimals,
        firstSeenTs: ts,
        lastSeenTs: ts,
        transferCount: 1,
        ownedWalletsTouched: Array.from(new Set(ownedTouch)),
      });
      continue;
    }

    prev.transferCount++;
    prev.firstSeenTs = Math.min(prev.firstSeenTs, ts);
    prev.lastSeenTs = Math.max(prev.lastSeenTs, ts);
    if (sym !== '?' && prev.tokenSymbol === '?') prev.tokenSymbol = sym;
    if (name && prev.tokenName === '?') prev.tokenName = name;
    const w = new Set(prev.ownedWalletsTouched);
    for (const a of ownedTouch) w.add(a);
    prev.ownedWalletsTouched = Array.from(w).sort();
  }

  return map;
}

export type TxHashAggregate = {
  hash: string;
  tokentx: Record<string, unknown>[];
  txlist: Record<string, unknown>[];
  internal: Record<string, unknown>[];
};

export function txHashFromExplorerRow(row: Record<string, unknown>): string {
  return String(row.hash ?? row.transactionHash ?? '').toLowerCase();
}

/**
 * Merge tokentx, txlist, and internal tx rows by normalized transaction hash.
 */
export function buildAggregatesByTxHash(
  tokenRows: Record<string, unknown>[],
  nativeRows: Record<string, unknown>[],
  internalRows: Record<string, unknown>[]
): Map<string, TxHashAggregate> {
  const m = new Map<string, TxHashAggregate>();

  function bucket(
    hash: string,
    which: 'tokentx' | 'txlist' | 'internal',
    row: Record<string, unknown>
  ): void {
    if (!hash) return;
    let g = m.get(hash);
    if (!g) {
      g = { hash, tokentx: [], txlist: [], internal: [] };
      m.set(hash, g);
    }
    g[which].push(row);
  }

  for (const row of tokenRows) {
    bucket(txHashFromExplorerRow(row), 'tokentx', row);
  }
  for (const row of nativeRows) {
    bucket(txHashFromExplorerRow(row), 'txlist', row);
  }
  for (const row of internalRows) {
    bucket(txHashFromExplorerRow(row), 'internal', row);
  }

  return m;
}

/**
 * Etherscan-style fields on txlist rows: `isError` ("0" ok / "1" failed), `txreceipt_status` ("1" ok / "0" fail).
 * Returns definite success, definite failure, or unknown (no row or ambiguous fields).
 */
export function txAppearsSuccessfulFromTxlistRows(
  txlistRows: Record<string, unknown>[]
): boolean | 'unknown' {
  if (txlistRows.length === 0) return 'unknown';
  const row = txlistRows[0];
  const err = String(row.isError ?? '').toLowerCase();
  const receipt = String(row.txreceipt_status ?? row.txReceiptStatus ?? '');
  if (err === '1') return false;
  if (receipt === '0') return false;
  if (err === '0' && receipt === '1') return true;
  return 'unknown';
}

export function summarizeAggregate(a: TxHashAggregate): {
  sourceRecordCount: number;
  sourceSummary: string;
  hasTransactionRecord: boolean;
  hasTokenTransferRecord: boolean;
  hasInternalTxRecord: boolean;
} {
  const hasTokenTransferRecord = a.tokentx.length > 0;
  const hasTransactionRecord = a.txlist.length > 0;
  const hasInternalTxRecord = a.internal.length > 0;
  const sourceRecordCount =
    a.tokentx.length + a.txlist.length + a.internal.length;
  const parts: string[] = [];
  if (hasTokenTransferRecord) parts.push(`${a.tokentx.length} token`);
  if (hasTransactionRecord) parts.push(`${a.txlist.length} tx`);
  if (hasInternalTxRecord) parts.push(`${a.internal.length} internal`);
  return {
    sourceRecordCount,
    sourceSummary: parts.length ? parts.join(', ') : 'none',
    hasTransactionRecord,
    hasTokenTransferRecord,
    hasInternalTxRecord,
  };
}

/**
 * Heuristic: multiple ERC-20 legs on the same hash involving an owned wallet often indicate a swap path.
 */
export function swapReviewFlagsForAggregate(
  a: TxHashAggregate,
  ownedSet: Set<string>
): string[] {
  if (a.tokentx.length < 2) return [];
  const contractsOut = new Set<string>();
  const contractsIn = new Set<string>();
  for (const row of a.tokentx) {
    const c = normalizeContractAddress(
      String(row.contractAddress ?? row.contract_address ?? '')
    );
    if (!c) continue;
    const from = String(row.from ?? '').toLowerCase();
    const to = String(row.to ?? '').toLowerCase();
    const oF = ownedSet.has(from);
    const oT = ownedSet.has(to);
    if (oF) contractsOut.add(c);
    if (oT) contractsIn.add(c);
  }
  let cross = false;
  const outList = Array.from(contractsOut);
  const inList = Array.from(contractsIn);
  for (const co of outList) {
    if (contractsIn.has(co)) continue;
    for (const ci of inList) {
      if (co !== ci) {
        cross = true;
        break;
      }
    }
    if (cross) break;
  }
  if (cross || (contractsOut.size >= 1 && contractsIn.size >= 1 && a.tokentx.length >= 2)) {
    return ['possible_swap'];
  }
  return [];
}
